import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { CaretDownFilled, CaretRightFilled, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatNumber } from '../../../../services/mixin';
import '../styles.scss';
import { Row } from '../../../../api/reportCustomer/reportCustomerApi';
import { IDataDefaulFilter } from '..';
import { selectorInfoUser } from '../../../../store/slices/infoUserSlice';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
    data?: Row[];
    brandColumns: string[];
    brandIdMap?: Record<string, number>;
    dataFilter?: IDataDefaulFilter;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
    n == null ? '' : formatNumber(n);

const toRecord = (values: any): Record<string, number> => {
    if (Array.isArray(values)) {
        return values.reduce((acc: Record<string, number>, v: any) => {
            const key = typeof v.title === 'string' ? v.title : String(v.id);
            acc[key] = typeof v.total === 'number' ? v.total : 0;
            return acc;
        }, {});
    }
    if (values && typeof values === 'object') {
        return Object.entries(values).reduce((acc: Record<string, number>, [k, v]) => {
            acc[k] = typeof v === 'number' ? v : 0;
            return acc;
        }, {});
    }
    return {};
};

const sumValues = (values?: Record<string, number>) =>
    values ? Object.values(values).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0) : 0;

interface NormalizedRow {
    id?: number;
    name: string;
    total?: number;
    values: Record<string, number>;
    valueIdMap: Record<string, number>;
    children?: NormalizedRow[];
}

const toIdMap = (values: any): Record<string, number> => {
    if (Array.isArray(values)) {
        return values.reduce((acc: Record<string, number>, v: any) => {
            const key = typeof v.title === 'string' ? v.title : String(v.id);
            acc[key] = v.id;
            return acc;
        }, {});
    }
    return {};
};

const normalizeRows = (rows: any[]): NormalizedRow[] =>
    rows.map(row => ({
        id: row.id,
        name: row.name,
        total: row.total,
        values: toRecord(row.values),
        valueIdMap: toIdMap(row.values),
        children: row.children?.length ? normalizeRows(row.children) : undefined,
    }));

type SortDir = 'asc' | 'desc' | null;
interface SortState { key: string; dir: SortDir }

const SortIcon: React.FC<{ colKey: string; sort: SortState }> = ({ colKey, sort }) => (
    <span className="ct-sort-icons">
        <CaretUpOutlined className={`ct-sort-up ${sort.key === colKey && sort.dir === 'asc' ? 'active' : ''}`} />
        <CaretDownOutlined className={`ct-sort-down ${sort.key === colKey && sort.dir === 'desc' ? 'active' : ''}`} />
    </span>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const COL_WIDTH_NAME = 270;
const COL_WIDTH_TOTAL = 100;
const COL_WIDTH_BRAND = 130;

// ─── Component ────────────────────────────────────────────────────────────────

const ComplaintsByBusinessBrandTable: React.FC<Props> = ({ data = [], brandColumns, brandIdMap = {}, dataFilter }) => {
    const navigate = useNavigate();
    const { unitTemp } = useSelector(selectorInfoUser);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [sort, setSort] = useState<SortState>({ key: '', dir: null });

    const handleCellClick = (row: NormalizedRow, brand: string) => {
        const categoryId = row.id;
        const brandId = row.valueIdMap?.[brand] ?? brandIdMap[brand];
        if (categoryId == null || brandId == null) return;

        navigate('/analysis-report/complaint-detail-list', {
            state: {
                apiType: 'businessTypeAndBrand',
                categoryId,
                brandId,
                dateCreate: dataFilter?.date,
                listUnit: dataFilter?.unitIds,
                unitScopeId: unitTemp,
                title: `Khiếu nại ${row.name} - ${brand}`,
            },
        });
    };

    const normalizedData = useMemo(() => normalizeRows(data), [data]);

    useEffect(() => {
        const collectExpandableKeys = (rows: NormalizedRow[], parentKey = ''): string[] => {
            return rows.flatMap(row => {
                const key = parentKey ? `${parentKey}::${row.name}` : row.name;
                if (!row.children?.length) return [];
                return [key, ...collectExpandableKeys(row.children, key)];
            });
        };
        setExpanded(new Set(collectExpandableKeys(normalizedData)));
    }, [normalizedData]);

    const toggleExpand = (name: string) =>
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });

    const handleSort = (key: string) => {
        setSort(prev => {
            if (prev.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            if (prev.dir === 'desc') return { key: '', dir: null };
            return { key, dir: 'asc' };
        });
    };

    const sortedData = useMemo(() => {
        if (!sort.key || !sort.dir) return normalizedData;
        return [...normalizedData].sort((a, b) => {
            let av: any, bv: any;
            if (sort.key === 'name') {
                av = a.name; bv = b.name;
            } else if (sort.key === 'total') {
                av = a.total ?? sumValues(a.values); bv = b.total ?? sumValues(b.values);
            } else {
                av = a.values?.[sort.key] ?? 0;
                bv = b.values?.[sort.key] ?? 0;
            }
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [sort, normalizedData]);

    const hasChildren = (row: NormalizedRow) =>
        row.children && row.children.length > 0;

    const renderRows = (rows: NormalizedRow[], depth: number = 0, parentKey: string = ''): React.ReactNode => {
        return rows.map((row, idx) => {
            const rowKey = parentKey ? `${parentKey}::${row.name}` : row.name;
            const isParent = hasChildren(row);
            const isExpanded = expanded.has(rowKey);

            return (
                <Fragment key={`${rowKey}-${idx}`}>
                    <tr className={isParent ? 'ct-parent-row' : 'ct-child-row'}>
                        <td
                            className={`ct-sticky-col ${isParent ? 'ct-parent-name' : 'ct-child-name'}`}
                            onClick={() => isParent && toggleExpand(rowKey)}
                            style={{ cursor: isParent ? 'pointer' : 'default', boxShadow: 'none' }}
                        >
                            <div className="ct-tree-cell" style={{ paddingLeft: depth * 16 }}>
                                {isParent ? (
                                    isExpanded
                                        ? <CaretDownFilled className="ct-expand-icon" />
                                        : <CaretRightFilled className="ct-expand-icon" />
                                ) : (
                                    <span className="ct-expand-placeholder" />
                                )}
                                <span className="ct-name-text">{row.name}</span>
                            </div>
                        </td>
                        <td className="ct-sticky-col-total ct-td-num" style={{ textAlign: 'center' }}>
                            {fmt(row.total ?? sumValues(row.values))}
                        </td>
                        {brandColumns.map(brand => (
                            <td key={brand} className="ct-td-num" style={{ cursor: 'pointer', color: '#096DD9' }}
                                onClick={() => handleCellClick(row, brand)}>
                                {fmt(row.values?.[brand])}
                            </td>
                        ))}
                    </tr>
                    {isParent && isExpanded && renderRows(row.children!, depth + 1, rowKey)}
                </Fragment>
            );
        });
    };

    return (
        <div className="ct-wrapper">
            <div className="ct-table-scroll">
                <table className="ct-table">
                    <colgroup>
                        <col style={{ width: COL_WIDTH_NAME }} />
                        <col style={{ width: COL_WIDTH_TOTAL }} />
                        {brandColumns.map(brand => (
                            <col key={brand} style={{ width: COL_WIDTH_BRAND }} />
                        ))}
                    </colgroup>

                    <thead>
                        <tr className="ct-thead-row1">
                            <th
                                className="ct-sticky-col ct-th-sortable"
                                style={{ width: COL_WIDTH_NAME, boxShadow: 'none' }}
                                onClick={() => handleSort('name')}
                            >
                                <div className="ct-th-inner">
                                    <span className="ct-th-label">Hạng mục</span>
                                    <SortIcon colKey="name" sort={sort} />
                                </div>
                            </th>
                            <th
                                className="ct-sticky-col-total ct-th-sortable"
                                style={{ width: COL_WIDTH_TOTAL, textAlign: 'center' }}
                                onClick={() => handleSort('total')}
                            >
                                <div className="ct-th-inner" style={{ justifyContent: 'center' }}>
                                    <span className="ct-th-label" style={{ textAlign: 'center' }}>Tổng</span>
                                    <SortIcon colKey="total" sort={sort} />
                                </div>
                            </th>
                            {brandColumns.map(brand => (
                                <th
                                    key={brand}
                                    className="ct-th-sortable"
                                    style={{ width: COL_WIDTH_BRAND }}
                                    onClick={() => handleSort(brand)}
                                >
                                    <div className="ct-th-inner">
                                        <span className="ct-th-label">{brand}</span>
                                        <SortIcon colKey={brand} sort={sort} />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {renderRows(sortedData)}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ComplaintsByBusinessBrandTable;
